/**
 * RelatedIncidentsSection — a compact list of incidents linked to a
 * specific project, plus a quick-file button that deep-links to the
 * create page with the project pre-selected. Rendered on the project
 * detail Overview tab.
 *
 * Kept intentionally small: shows the 5 most recent, links to the
 * full list filtered by this project for the rest. If there are zero
 * we still render a card with the "Report Incident" CTA so the
 * project detail page always makes reporting one click away.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShieldAlert, HeartPulse, AlertTriangle, Wrench, Plus, ChevronRight, CheckCircle2 } from "lucide-react";

const TYPE_META = {
  stop_work:       { label: "Stop-Work",       icon: ShieldAlert,   tone: "red" },
  injury:          { label: "Injury",          icon: HeartPulse,    tone: "red" },
  near_miss:       { label: "Near Miss/Good Catch", icon: AlertTriangle, tone: "amber" },
  property_damage: { label: "Property Damage", icon: Wrench,        tone: "amber" },
};
const STATUS_CLS = {
  open:          "bg-red-900/60 text-red-200 border-red-800",
  investigating: "bg-amber-900/60 text-amber-200 border-amber-800",
  resolved:      "bg-emerald-900/60 text-emerald-200 border-emerald-800",
  closed:        "bg-slate-800 text-slate-300 border-slate-700",
};

export default function RelatedIncidentsSection({ projectId }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!projectId) return;
    api.get(`/incidents?project_id=${encodeURIComponent(projectId)}&limit=5`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [projectId]);

  if (items === null) return null; // loading — quiet

  const openCount = items.filter((i) => i.status === "open" || i.status === "investigating").length;

  return (
    <Card
      className={`${openCount > 0 ? "bg-red-950/20 border-red-900" : "bg-slate-900 border-slate-800"}`}
      data-testid="related-incidents-section"
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <div className={`p-2 rounded-md border shrink-0 ${openCount > 0 ? "border-red-800 bg-red-950/40 text-red-300" : "border-slate-800 bg-slate-950 text-slate-400"}`}>
              <ShieldAlert className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.25em] uppercase text-slate-400 font-display font-bold">
                Related Safety Incidents
              </div>
              <div className="font-display font-bold text-lg tracking-tight">
                {items.length === 0
                  ? "No incidents logged for this project"
                  : `${items.length} logged${openCount > 0 ? ` · ${openCount} active` : ""}`}
              </div>
            </div>
          </div>
          <Button
            asChild
            size="sm"
            className="bg-red-700 hover:bg-red-600 font-display tracking-wider"
            data-testid="related-incidents-report-btn"
          >
            <Link to={`/incidents/new?project=${projectId}`}>
              <Plus className="w-4 h-4 mr-1" /> Report
            </Link>
          </Button>
        </div>

        {items.length === 0 ? (
          <div className="text-xs text-slate-400 flex items-center gap-1.5" data-testid="related-incidents-empty">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
            All clear — no stop-works, near-misses, or injuries logged against this project.
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="related-incidents-list">
            {items.map((it) => {
              const meta = TYPE_META[it.type] || TYPE_META.near_miss;
              const Icon = meta.icon;
              return (
                <Link
                  key={it.id}
                  to={`/incidents/${it.id}`}
                  className="flex items-center gap-2.5 p-2 rounded-md border border-slate-800 bg-slate-950/60 hover:bg-slate-900 transition-colors"
                  data-testid={`related-incident-${it.id}`}
                >
                  <Icon className={`w-4 h-4 shrink-0 ${meta.tone === "red" ? "text-red-400" : "text-amber-400"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-100 truncate">
                      <span className="font-display font-bold mr-2">{meta.label}</span>
                      <span className="text-slate-300">{it.description}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono">
                      {it.reported_by_name} · {(it.reported_at || "").slice(0, 10)}
                    </div>
                  </div>
                  <span className={`inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 ${STATUS_CLS[it.status]}`}>
                    {it.status}
                  </span>
                  <ChevronRight className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                </Link>
              );
            })}
            <Link
              to={`/incidents?project=${projectId}`}
              className="text-xs text-blue-400 hover:text-blue-200 underline underline-offset-2 inline-block pt-1"
              data-testid="related-incidents-see-all"
            >
              See all incidents for this project →
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
